import { insightsIntl } from "../strings/intl";
import { useSuspenseQuery } from "@tanstack/react-query";
import { type IntlShape, useIntl } from "react-intl";
import type { LegacyTableColumn, ReferencedChartTabProps } from "@wayflyer/flyui-dataviz";
import * as React from "react";
import { useLoaderData } from "react-router";
import { styled } from "styled-components";

import { MetricTypes } from "../autogen/metrics";
import { metricDescription, metricTooltip } from "../strings/metric-messages";
import type { AdditionalCostsData } from "../components/shared";
import type { OverviewProps } from "../interface";
import type { InsightsLoaderReturn } from "../loader";
import { formatValue } from "../strings/type-formatting";
import { getUrls } from "../urls";
import {
  getAdditionalCostsData,
  initialAdditionalCosts,
  storeAdditionalCostsData,
  useAdditionalCosts,
} from "../utils/additional-costs-api";
import { allHistoricalData, yoyReferenceForTimeRange } from "../utils/custom-time-range-helpers";
import { dateRangePresets, useDateAndTemp } from "../utils/date";
import { type DashboardOutput, getDashboardOutput } from "../utils/fetch-api";
import fetchDashboardMetaData, { type DashboardMetadata, getEmptyMetadata } from "../utils/fetch-dashboard-metadata";
import { getCurrencySymbol } from "../utils/metrics";
import { insightsRouteDataQuery, internalInsightsRouteDataQuery } from "../utils/route-data-query";
import type { MetricType, TemporalResolution } from "../utils/types";

import { aggregateOverviewSummaryData, getInsightsOverviewChartAndTableData } from "./overview-datapoints-2";
import { usePinnedMetrics } from "./pinned-metrics";
import { Overview } from "./screen";

const SUPPORT_LINK = "mailto:support@wayflyer.com";
const LEARN_MORE_LINK = "https://www.wayflyer.com/solutions/insights";

const INITIAL_PAGE_SIZE = 20;

const getToolTipForColumn = (intl: IntlShape) => (columnId: LegacyTableColumn["id"]) => {
  if (columnId === "period") {
    return null;
  } else {
    return metricTooltip(intl, columnId) ?? null;
  }
};

const HintContainer = styled.span`
  margin: 0 20px;
`;

const getInputMetrics = () => {
  return [
    MetricTypes.TOTAL_UNIQUE_NEW_CUSTOMER_COUNT,
    MetricTypes.MARKETING_SPEND,
    // MetricTypes.GROSS_REVENUE_LESS_DISCOUNTS_LESS_REFUNDS_FROM_ORDERS_PLUS_SHIPPING_PLUS_TAX,
    // MetricTypes.GROSS_REVENUE_LESS_DISCOUNTS_LESS_REFUNDS_BY_REFUNDED_DATE_PLUS_SHIPPING_PLUS_TAX,
    MetricTypes.GROSS_REVENUE_LESS_DISCOUNTS_PLUS_SHIPPING_PLUS_TAX,
    MetricTypes.ROAS,
    MetricTypes.CAC,
    MetricTypes.AOV,
    MetricTypes.TOTAL_ORDER_QUANTITY,
    MetricTypes.TOTAL_ORDERED_ITEM_QUANTITY_LESS_REFUNDED_ITEM_QUANTITY,
    MetricTypes.TOTAL_ORDERED_ITEM_QUANTITY_LESS_REFUNDED_ITEM_QUANTITY_BY_REFUNDED_DATE,
  ].map((metric) => {
    return {
      title: metricDescription(insightsIntl, metric),
      metricType: metric,
    };
  });
};

const getSummaryChartTabs = (intl: IntlShape) => {
  return [
    MetricTypes.GROSS_REVENUE_LESS_DISCOUNTS_PLUS_SHIPPING_PLUS_TAX,
    MetricTypes.MARKETING_SPEND,
    MetricTypes.ROAS,
    MetricTypes.AOV,
    MetricTypes.CAC,
  ].map((metric) => {
    return {
      title: metricDescription(insightsIntl, metric),
      // MARKETING_SPEND used to special-case a dedicated "Overview marketing spend tooltip" key, which the
      // catalog had no copy for, so it rendered the key itself. Dropped, like the other tooltips with none.
      tooltip: metricTooltip(intl, metric),
      metricType: metric,
    };
  });
};

const getTableTabs = (intl: IntlShape) => {
  return [
    MetricTypes.GROSS_REVENUE_LESS_DISCOUNTS_PLUS_SHIPPING_PLUS_TAX,
    MetricTypes.MARKETING_SPEND,
    MetricTypes.ROAS,
    MetricTypes.AOV,
    MetricTypes.CAC,
  ].map((metric) => {
    return {
      title: metricDescription(insightsIntl, metric),
      tooltip: metricTooltip(intl, metric),
      metricType: metric,
    };
  });
};

const OVERVIEW_METRICS = [
  MetricTypes.GROSS_REVENUE_LESS_DISCOUNTS_PLUS_SHIPPING_PLUS_TAX,
  MetricTypes.MARKETING_SPEND,
  MetricTypes.ROAS,
  MetricTypes.AOV,
  MetricTypes.CAC,
  MetricTypes.TOTAL_ORDER_QUANTITY,
  MetricTypes.TOTAL_UNIQUE_NEW_CUSTOMER_COUNT,
  MetricTypes.TOTAL_ORDERED_ITEM_QUANTITY_LESS_REFUNDED_ITEM_QUANTITY,
  MetricTypes.TOTAL_ORDERED_ITEM_QUANTITY_LESS_REFUNDED_ITEM_QUANTITY_BY_REFUNDED_DATE,
];

const getIncrementString = (resolution: TemporalResolution): "DoD" | "WoW" | "MoM" => {
  switch (resolution.resolution) {
    case "DAILY":
      return "DoD";
    case "MONTHLY":
      return "MoM";
    case "WEEKLY":
    default:
      return "WoW";
  }
};

/**
 * Entry point
 */
const OverviewDashboard = ({ internal = false }) => {
  const intl = useIntl();
  const { customerDomain }: InsightsLoaderReturn = useLoaderData();
  const routeData = useSuspenseQuery(
    (internal ? internalInsightsRouteDataQuery(customerDomain) : insightsRouteDataQuery(customerDomain)) as ReturnType<
      typeof insightsRouteDataQuery
    >
  );

  const { showAmazonPlaceholder, isAmazonCustomer } = routeData.data;
  const { dashboardDataPath, additionalCostsPath, dashboardConfigurationPath } = getUrls(customerDomain);
  const [isLoadingRawData, setIsLoadingRawData] = React.useState(false);
  const [requiresDataFetch, setRequiresDataFetch] = React.useState(true);
  const [isLoadingChartData, setIsLoadingChartData] = React.useState(false);
  const [requiresDataComputation, setRequiresDataComputation] = React.useState(false);
  const [errorChartData, setErrorChartData] = React.useState<string | null>(null);

  const [additionalCostsDataError, setAdditionalCostsDataError] = React.useState<string | null>(null);

  // Chart data enabled
  const [
    grossRevenueLessDiscountLessRefundsPlusShippingPlusTaxEnabled,
    setGrossRevenueLessDiscountLessRefundsPlusShippingPlusTaxEnabled,
  ] = React.useState(false);
  const [grossRevenueLessDiscountPlusShippingPlusTaxEnabled, setGrossRevenueLessDiscountPlusShippingPlusTaxEnabled] =
    React.useState(true);
  const [profitEnabled, setProfitEnabled] = React.useState(true);
  const [marketingEnabled, setMarketingEnabled] = React.useState(false);
  const [roasEnabled, setROASEnabled] = React.useState(!isAmazonCustomer);
  const [aovEnabled, setAOVEnabled] = React.useState(false);
  const [cacEnabled, setCACEnabled] = React.useState(false);

  const [rawCurrentData, setRawCurrentData] = React.useState<DashboardOutput | null>(null);
  const [rawReferenceData, setRawReferenceData] = React.useState<DashboardOutput | null>(null);
  const [rawChartData, setRawChartData] = React.useState<DashboardOutput | null>(null);
  const [customerMetadata, setCustomerMetadata] = React.useState<DashboardMetadata>(getEmptyMetadata());

  const additionalCosts = React.useRef<AdditionalCostsData>({ ...initialAdditionalCosts });
  const additionalCostsApiCalled = React.useRef(false);
  const currencyCodeRef = React.useRef("USD");
  const [localAdditionalCosts, setLocalAdditionalCosts] = useAdditionalCosts(additionalCostsPath);

  const [selectedCurrentTimeRangeEnabled, setSelectedCurrentTimeRangeEnabled] = React.useState(true);
  const [selectedReferenceTimeRangeEnabled, setSelectedReferenceTimeRangeEnabled] = React.useState(false);

  const onChange = () => {
    setRequiresDataFetch(true);
  };

  const {
    selectedTimeRange,
    handleTimeRangeChange,
    currentPreset,
    temporalResolution,
    selectedButtonIndex,
    hideWeekly,
    hideMonthly,
    handleTempResChange,
  } = useDateAndTemp(onChange, onChange);

  const pointOnPointIncrementString = getIncrementString(temporalResolution);

  const [currencySymbol, setCurrencySymbol] = React.useState("$");

  const [tableData, setTableData] = React.useState<OverviewProps["tableProps"]>({
    data: [],
    columns: [],
  });
  const [chartsData, setChartsData] = React.useState<OverviewProps["chartData"]>(null);
  const [summaryData, setSummaryData] = React.useState<ReferencedChartTabProps[]>(getSummaryChartTabs(intl));

  const formatChartValue = (value: number | string, isROAS?: boolean) => {
    const metricType: MetricType = isROAS
      ? { type: "RATE" }
      : { type: "CURRENCY", currencyCode: currencyCodeRef.current };
    return formatValue({ value: value.toString(), metricType });
  };

  const isMetricEnabled = (metricType: MetricTypes) => {
    switch (metricType) {
      case MetricTypes.GROSS_REVENUE_LESS_DISCOUNTS_PLUS_SHIPPING_PLUS_TAX:
        return grossRevenueLessDiscountPlusShippingPlusTaxEnabled;
      case MetricTypes.GROSS_REVENUE_LESS_DISCOUNTS_LESS_REFUNDS_FROM_ORDERS_PLUS_SHIPPING_PLUS_TAX:
        return grossRevenueLessDiscountLessRefundsPlusShippingPlusTaxEnabled;
      case MetricTypes.MARKETING_SPEND:
        return marketingEnabled;
      case MetricTypes.ROAS:
        return roasEnabled;
      case MetricTypes.AOV:
        return aovEnabled;
      case MetricTypes.CAC:
        return cacEnabled;
      default:
        return profitEnabled;
    }
  };

  const allRawData = React.useMemo(
    () => [customerMetadata, rawChartData, rawCurrentData, rawReferenceData],
    [customerMetadata, rawCurrentData, rawReferenceData, rawChartData]
  );

  React.useEffect(() => {
    if (allRawData.every((x) => x)) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- Existing violation after eslint-plugin-react-hooks v7 upgrade; remove when fixed.
      setIsLoadingRawData(false);
    }
  }, [allRawData]);

  const scheduleInsightsOverviewComputation = React.useCallback(() => {
    setIsLoadingChartData(true);
    setRequiresDataComputation(true);
  }, []);

  const updateProfitData = React.useCallback(() => {
    if (additionalCosts.current && !isLoadingRawData) {
      scheduleInsightsOverviewComputation();
    }
  }, [scheduleInsightsOverviewComputation, isLoadingRawData]);

  React.useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- Existing violation after eslint-plugin-react-hooks v7 upgrade; remove when fixed.
    setIsLoadingRawData(true);
    setIsLoadingChartData(true);
    setErrorChartData(null);

    fetchDashboardMetaData(
      dashboardDataPath,
      (data) => {
        setCustomerMetadata(data);
      },
      (error) => {
        setErrorChartData(error);
      }
    );
  }, [dashboardDataPath]);

  const computeInsightsOverviewData = () => {
    if (
      !requiresDataComputation ||
      requiresDataFetch ||
      isLoadingRawData ||
      !rawChartData ||
      !rawCurrentData ||
      !rawReferenceData
    ) {
      return;
    }

    const chartAndTableData = getInsightsOverviewChartAndTableData(
      selectedTimeRange,
      rawChartData,
      temporalResolution,
      getInputMetrics(),
      getTableTabs(intl),
      additionalCosts.current
    );
    setChartsData({
      data: chartAndTableData.chartData,
      formatValue: formatChartValue,
      resolutionAbreviation: pointOnPointIncrementString,
    });
    setTableData({
      columns: chartAndTableData.tableData.columns,
      data: chartAndTableData.tableData.data,
      initialPageSize: INITIAL_PAGE_SIZE,
      enablePagination: chartAndTableData.tableData.data.length > INITIAL_PAGE_SIZE,
      sortBy: [{ id: "period", desc: true }],
      getToolTipForColumn: getToolTipForColumn(intl),
    });
    setSummaryData(
      aggregateOverviewSummaryData(
        rawCurrentData,
        rawReferenceData,
        selectedTimeRange,
        getSummaryChartTabs(intl),
        additionalCosts.current,
        customerMetadata
      )
    );
    setCurrencySymbol(getCurrencySymbol(currencyCodeRef.current));
    setIsLoadingChartData(false);
    setRequiresDataComputation(false);
  };
  // eslint-disable-next-line react-hooks/refs -- Existing violation after eslint-plugin-react-hooks v7 upgrade; remove when fixed.
  computeInsightsOverviewData();

  const fetchCurrentSummaryData = React.useCallback(() => {
    getDashboardOutput(
      dashboardDataPath,
      [
        {
          metrics: OVERVIEW_METRICS,
          filename: "overview_insights",
          groupBys: [],
        },
      ],
      selectedTimeRange,
      (data) => {
        setRawCurrentData(data);
      },
      null,
      (error) => {
        setIsLoadingChartData(false);
        setErrorChartData(error);
      },
      temporalResolution
    );
  }, [dashboardDataPath, selectedTimeRange, temporalResolution]);

  const fetchReferenceSummaryData = React.useCallback(() => {
    getDashboardOutput(
      dashboardDataPath,
      [
        {
          metrics: OVERVIEW_METRICS,
          filename: "overview_insights",
          groupBys: [],
        },
      ],
      yoyReferenceForTimeRange(selectedTimeRange, currentPreset),
      (data) => {
        setRawReferenceData(data);
      },
      null,
      (error) => {
        setIsLoadingChartData(false);
        setErrorChartData(error);
      },
      temporalResolution
    );
  }, [dashboardDataPath, selectedTimeRange, temporalResolution, currentPreset]);

  const fetchChartData = React.useCallback(() => {
    getDashboardOutput(
      dashboardDataPath,
      [
        {
          metrics: OVERVIEW_METRICS,
          filename: "overview_insights",
          groupBys: [],
        },
      ],
      allHistoricalData(selectedTimeRange),
      (data) => {
        currencyCodeRef.current = data.currencyCode;
        setRawChartData(data);
      },
      null,
      (error) => {
        setIsLoadingChartData(false);
        setErrorChartData(error);
      },
      temporalResolution
    );
  }, [dashboardDataPath, selectedTimeRange, temporalResolution]);

  React.useEffect(() => {
    if (requiresDataFetch) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- Existing violation after eslint-plugin-react-hooks v7 upgrade; remove when fixed.
      setRawChartData(null);
      setRawCurrentData(null);
      setRawReferenceData(null);

      fetchCurrentSummaryData();
      fetchReferenceSummaryData();
      fetchChartData();
      setIsLoadingRawData(true);
      setRequiresDataFetch(false);
      scheduleInsightsOverviewComputation();
    }
  }, [
    requiresDataFetch,
    fetchChartData,
    fetchCurrentSummaryData,
    fetchReferenceSummaryData,
    scheduleInsightsOverviewComputation,
  ]);

  const handleThisYearSwitch = (isOn: boolean) => {
    setSelectedCurrentTimeRangeEnabled(isOn);
  };

  const handleLastYearSwitch = (isOn: boolean) => {
    setSelectedReferenceTimeRangeEnabled(isOn);
  };

  const handleChartEnabled = (metricType: string, selected: boolean) => {
    switch (metricType) {
      case MetricTypes.GROSS_REVENUE_LESS_DISCOUNTS_PLUS_SHIPPING_PLUS_TAX:
        setGrossRevenueLessDiscountPlusShippingPlusTaxEnabled(selected);
        break;
      case MetricTypes.GROSS_REVENUE_LESS_DISCOUNTS_LESS_REFUNDS_FROM_ORDERS_PLUS_SHIPPING_PLUS_TAX:
      case MetricTypes.GROSS_REVENUE_LESS_DISCOUNTS_LESS_REFUNDS_BY_REFUNDED_DATE_PLUS_SHIPPING_PLUS_TAX:
        setGrossRevenueLessDiscountLessRefundsPlusShippingPlusTaxEnabled(selected);
        break;
      case MetricTypes.PROFIT:
        setProfitEnabled(selected);
        break;
      case MetricTypes.MARKETING_SPEND:
        setMarketingEnabled(selected);
        break;
      case MetricTypes.ROAS:
        setROASEnabled(selected);
        break;
      case MetricTypes.AOV:
        setAOVEnabled(selected);
        break;
      case MetricTypes.CAC:
        setCACEnabled(selected);
        break;
    }
  };

  React.useEffect(() => {
    additionalCosts.current = localAdditionalCosts;
    updateProfitData();
  }, [localAdditionalCosts, updateProfitData]);

  const handleAdditionalCostsSave = (additionalCostsData: AdditionalCostsData): Promise<any> => {
    return new Promise((resolve, reject) => {
      storeAdditionalCostsData(
        additionalCostsPath,
        additionalCostsData,
        () => {
          setLocalAdditionalCosts(additionalCostsData);
          resolve(null);
        },
        () => {
          reject();
        }
      );
    });
  };

  const handleAdditionalCostsUpdate = (field: keyof AdditionalCostsData, value: number) => {
    additionalCosts.current[field] = value;
    updateProfitData();
  };

  React.useEffect(() => {
    if (additionalCostsApiCalled.current) {
      return;
    }
    additionalCostsApiCalled.current = true;
    const finallyCB = () => {
      if (!isLoadingRawData) {
        scheduleInsightsOverviewComputation();
      }
    };
    getAdditionalCostsData(
      additionalCostsPath,
      (additionalCostsData: AdditionalCostsData) => {
        additionalCosts.current = additionalCostsData;
        setLocalAdditionalCosts(additionalCostsData);
        setAdditionalCostsDataError(null);
        finallyCB();
      },
      (failureMessage: string) => {
        additionalCostsApiCalled.current = false;
        setAdditionalCostsDataError(failureMessage);
        additionalCosts.current = { ...initialAdditionalCosts };
        finallyCB();
      }
    );
  }, [scheduleInsightsOverviewComputation, additionalCostsPath, isLoadingRawData, setLocalAdditionalCosts]);

  const metrics = usePinnedMetrics(
    selectedTimeRange,
    summaryData,
    dashboardConfigurationPath,
    dashboardDataPath,
    customerMetadata,
    currentPreset
  );
  const pinnedTabsProps = {
    pinnedMetricsProps: {
      title: "",
      metrics,
      loading: !metrics.length,
      dropdownLabel: insightsIntl.formatMessage({ id: "Choose key metrics", defaultMessage: "Choose key metrics" }),
    },
    enableTabs: true,
    tabTitlePinnedMetrics: insightsIntl.formatMessage({ id: "Key Metrics", defaultMessage: "Key Metrics" }),
    tabTitleTable: insightsIntl.formatMessage({ id: "Detailed Performance", defaultMessage: "Detailed Performance" }),
  };

  return (
    <Overview
      datePickerHeaderProps={{
        currentPreset,
        startDate: selectedTimeRange.start.format("YYYY-MM-DD"),
        endDate: selectedTimeRange.end.format("YYYY-MM-DD"),
        dateRangePresets: dateRangePresets,
        onChange: handleTimeRangeChange,
      }}
      pageTitle={insightsIntl.formatMessage({ id: "Overview dashboard", defaultMessage: "Overview dashboard" })}
      subtitle=""
      hint={
        <HintContainer>
          <strong>
            {insightsIntl.formatMessage({
              id: "Here's the summary of your store performance",
              defaultMessage: "Here's the summary of your store performance",
            })}
          </strong>
          <br />
          {insightsIntl.formatMessage({
            id: "Add any additional costs to view your profit chart.",
            defaultMessage: "Add any additional costs to view your profit chart.",
          })}
        </HintContainer>
      }
      tableProps={tableData}
      chartFilters={{
        switchOneLabel: insightsIntl.formatMessage({ id: "chart filter - This year", defaultMessage: "This year" }),
        switchTwoLabel: insightsIntl.formatMessage({ id: "chart filter - Last year", defaultMessage: "Last year" }),
        switchesDisabled: isLoadingChartData,
        hideWeekly,
        hideMonthly,
        onSwitchOne: handleThisYearSwitch,
        onSwitchTwo: handleLastYearSwitch,
        onClickDateRes: handleTempResChange,
        selectedButtonIndex: selectedButtonIndex,
      }}
      chartData={chartsData}
      thisYearEnabled={selectedCurrentTimeRangeEnabled}
      lastYearEnabled={selectedReferenceTimeRangeEnabled}
      salesEnabled={grossRevenueLessDiscountLessRefundsPlusShippingPlusTaxEnabled}
      salesIncReturnsEnabled={grossRevenueLessDiscountPlusShippingPlusTaxEnabled}
      profitEnabled={profitEnabled}
      marketingEnabled={marketingEnabled}
      roasEnabled={roasEnabled}
      aovEnabled={aovEnabled}
      cacEnabled={cacEnabled}
      chartDataLoading={isLoadingChartData}
      chartDataError={errorChartData}
      summaryData={summaryData.map((item) => {
        return {
          ...item,
          selected: isMetricEnabled(item.metricType as MetricTypes),
        };
      })}
      summaryDataLoading={isLoadingChartData}
      summaryDataError={errorChartData}
      // eslint-disable-next-line react-hooks/refs -- Existing violation after eslint-plugin-react-hooks v7 upgrade; remove when fixed.
      additionalCostsData={{
        currencySymbol,
        saveHandler: handleAdditionalCostsSave,
        // eslint-disable-next-line react-hooks/refs -- Existing violation after eslint-plugin-react-hooks v7 upgrade; remove when fixed.
        additionalCostsData: additionalCosts.current || { ...initialAdditionalCosts },
        onChangeHandler: handleAdditionalCostsUpdate,
      }}
      additionalCostsDataError={additionalCostsDataError}
      onSelectMetric={handleChartEnabled}
      {...pinnedTabsProps}
      showAmazonPlaceholder={showAmazonPlaceholder}
      isAmazonCustomer={isAmazonCustomer}
      learnMoreLink={LEARN_MORE_LINK}
      supportLink={SUPPORT_LINK}
    />
  );
};

export const CustomerOverviewDashboard = () => {
  return <OverviewDashboard internal={false} />;
};

export const InternalOverviewDashboard = () => {
  return <OverviewDashboard internal={true} />;
};
